export function normalizeClipboardHTML(html: string): string {
  const box = document.createElement('div')
  box.innerHTML = html

  // 1) Remove stuff that we don't need
  box.querySelectorAll('meta,script,style,title').forEach((n) => n.remove())

  // 2) Flatten span (keep only content)
  box.querySelectorAll('span').forEach((span) => {
    const parent = span.parentNode!
    while (span.firstChild) parent.insertBefore(span.firstChild, span)
    parent.removeChild(span)
  })

  // 3) Convert newlines in text nodes to <br> elements
  const convertNewlinesToBr = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || ''
      if (text.includes('\n')) {
        const parent = node.parentNode!
        const fragment = document.createDocumentFragment()
        const lines = text.split('\n')

        lines.forEach((line, index) => {
          if (index > 0) {
            fragment.appendChild(document.createElement('br'))
          }
          if (line.length > 0) {
            fragment.appendChild(document.createTextNode(line))
          }
        })

        parent.replaceChild(fragment, node)
      }
    } else {
      // Process child nodes recursively
      Array.from(node.childNodes).forEach(convertNewlinesToBr)
    }
  }

  convertNewlinesToBr(box)

  // Extract plain text structure from complex HTML (like chat interface)
  const extractTextStructure = (container: HTMLElement) => {
    // Check if this looks like a complex UI structure
    const complexIndicators = [
      'div[class*="data-v"]', // Vue components
      'div[style*="transform"]', // Transformed elements
      'div[class*="chat-"]', // Chat interface elements
      'div[class*="virtual-scroll"]', // Virtual scroll elements
      'div[class*="message-"]', // Message elements
      '[class*="d-flex"]', // Utility classes
      '[style*="position: absolute"]', // Absolutely positioned elements
    ]

    const complexElements = complexIndicators.reduce((count, selector) => {
      return count + container.querySelectorAll(selector).length
    }, 0)

    const hasComplexStructure = complexElements > 2

    if (!hasComplexStructure) {
      return // Keep existing structure
    }

    // Create a simplified structure
    const simplified = document.createElement('div')

    const extractTextRecursively = (node: Node, targetContainer: HTMLElement) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || ''
        // Preserve spaces, but clean up excessive whitespace
        const cleanedText = text.replace(/\s+/g, ' ')
        if (cleanedText.trim().length > 0) {
          targetContainer.appendChild(document.createTextNode(cleanedText))
        } else if (cleanedText.includes(' ')) {
          // Preserve single space if it's just whitespace
          targetContainer.appendChild(document.createTextNode(' '))
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement

        if (element.tagName === 'BR') {
          targetContainer.appendChild(document.createElement('br'))
        } else {
          // Skip interface elements that don't contain useful content
          const skipSelectors = [
            'chat-message-date', // Date/time elements
            'hide-on-hover', // UI interaction elements
            'no-print', // Print-specific elements
            'system-message', // System messages
          ]

          const shouldSkip = skipSelectors.some(
            (selector) => element.classList.contains(selector) || element.className.includes(selector),
          )

          if (!shouldSkip) {
            // For other elements, process their children
            Array.from(element.childNodes).forEach((child) => {
              extractTextRecursively(child, targetContainer)
            })
          }
        }
      }
    }

    Array.from(container.childNodes).forEach((child) => {
      extractTextRecursively(child, simplified)
    })

    // Replace container content with simplified structure
    container.replaceChildren(...simplified.childNodes)
  }

  extractTextStructure(box)

  // 4) Preprocess consecutive <br> elements to handle empty lines correctly
  const preprocessConsecutiveBrs = (container: HTMLElement) => {
    const nodes = Array.from(container.childNodes)
    let lastWasBr = false

    for (let i = 0; i < nodes.length; i += 1) {
      const node = nodes[i]

      if (node.nodeName === 'BR') {
        if (lastWasBr) {
          // This is a second consecutive BR - insert a marker for empty line
          const emptyLineMarker = document.createElement('span')
          emptyLineMarker.setAttribute('data-empty-line', 'true')
          emptyLineMarker.style.display = 'none'
          container.insertBefore(emptyLineMarker, node)
        }
        lastWasBr = true
      } else if (
        node.nodeType === Node.ELEMENT_NODE ||
        (node.nodeType === Node.TEXT_NODE && node.textContent?.trim())
      ) {
        lastWasBr = false
      }
      // Ignore whitespace-only text nodes for BR sequence detection
    }
  }

  preprocessConsecutiveBrs(box)

  // 5) Collect paragraphs.
  //    Single <br> -> line break inside <p>
  //    Double (and more) <br><br> -> empty paragraph <p><br></p>
  const out = document.createElement('div')
  let p = document.createElement('p')
  let brStreak = 0
  let hasContent = false // Track if we've added any content

  const pushCurrentP = () => {
    // Only add paragraph if it has content
    if (p.childNodes.length > 0) {
      out.appendChild(p)
      hasContent = true
    }
    p = document.createElement('p')
    brStreak = 0
  }

  const pushEmptyParagraph = () => {
    // Don't add empty paragraphs if we haven't added any content yet
    if (hasContent) {
      const emptyP = document.createElement('p')
      emptyP.appendChild(document.createElement('br'))
      out.appendChild(emptyP)
    }
    brStreak = 0
  }

  const cleanClone = (node: Node) => {
    // Skip empty line markers
    if (node.nodeType === Node.ELEMENT_NODE && (node as HTMLElement).hasAttribute('data-empty-line')) {
      return document.createTextNode('')
    }

    const clone = node.cloneNode(true) as HTMLElement
    if (clone.nodeType === Node.ELEMENT_NODE) {
      const el = clone as HTMLElement
      // remove inline handlers and inline styles
      for (const attr of Array.from(el.attributes)) {
        if (attr.name.startsWith('on') || attr.name === 'style') {
          el.removeAttribute(attr.name)
        }
      }
      if (el.tagName === 'A') {
        const href = el.getAttribute('href') || ''
        if (!/^(https?:|mailto:|tel):/i.test(href)) {
          el.removeAttribute('href')
        }
        el.setAttribute('rel', 'noopener noreferrer')
        el.setAttribute('target', '_blank')
      }
      if (el.tagName === 'BR') {
        el.getAttributeNames?.().forEach((a) => el.removeAttribute(a))
      }
    }
    return clone
  }

  // Process nodes - handle existing paragraphs and other elements
  const nodes = Array.from(box.childNodes)

  for (let i = 0; i < nodes.length; i += 1) {
    const node = nodes[i]

    if (node.nodeName === 'BR') {
      brStreak += 1

      if (brStreak === 1) {
        // first <br> - close current paragraph and start new one
        pushCurrentP()
      } else {
        // consecutive <br> - insert empty paragraph
        pushEmptyParagraph()
      }
    } else if (node.nodeName === 'P') {
      // Handle existing paragraph elements
      const pElement = node as HTMLElement
      const cleanedP = cleanClone(pElement) as HTMLElement

      // Check if paragraph is empty (only whitespace and/or <br>)
      const textContent = cleanedP.textContent?.trim() || ''
      const isEmpty = textContent.length === 0

      if (isEmpty) {
        // Empty paragraph should be added if we already have content
        if (hasContent) {
          // Close current paragraph if it has content
          if (p.childNodes.length > 0) {
            pushCurrentP()
          }
          // Add empty paragraph
          const emptyP = document.createElement('p')
          emptyP.appendChild(document.createElement('br'))
          out.appendChild(emptyP)
        }
        brStreak = 0
      } else {
        // Reset br streak and add non-empty paragraph content to current paragraph
        brStreak = 0

        // Close current paragraph if it has content
        if (p.childNodes.length > 0) {
          pushCurrentP()
        }

        // Add the cleaned paragraph directly
        out.appendChild(cleanedP)
        hasContent = true
      }
    } else if (node.nodeType === Node.ELEMENT_NODE && (node as HTMLElement).hasAttribute('data-empty-line')) {
      // Handle empty line markers
      if (hasContent) {
        // Close current paragraph if it has content
        if (p.childNodes.length > 0) {
          pushCurrentP()
        }
        // Add empty paragraph
        const emptyP = document.createElement('p')
        emptyP.appendChild(document.createElement('br'))
        out.appendChild(emptyP)
      }
      brStreak = 0
    } else {
      // Handle other elements (text nodes, etc.)
      brStreak = 0
      const clonedNode = cleanClone(node)

      // Check if this adds meaningful content
      if (
        clonedNode.textContent?.trim() ||
        (clonedNode.nodeType === Node.ELEMENT_NODE && clonedNode.childNodes.length > 0)
      ) {
        hasContent = true
      }

      p.appendChild(clonedNode)
    }
  }

  // add final paragraph only if it has content
  if (p.childNodes.length > 0) {
    out.appendChild(p)
    hasContent = true
  }

  // Final cleanup: remove empty paragraphs at the beginning and end
  const paragraphs = Array.from(out.children) as HTMLElement[]

  // Helper function to check if paragraph is empty (only contains <br> or whitespace)
  const isEmpty = (paragraph: HTMLElement): boolean => {
    if (paragraph.tagName !== 'P') return false
    const textContent = paragraph.textContent?.trim() || ''
    return textContent.length === 0
  }

  // Find first and last non-empty paragraphs
  let firstNonEmpty = 0
  let lastNonEmpty = paragraphs.length - 1

  while (firstNonEmpty < paragraphs.length && isEmpty(paragraphs[firstNonEmpty])) {
    firstNonEmpty += 1
  }

  while (lastNonEmpty >= 0 && isEmpty(paragraphs[lastNonEmpty])) {
    lastNonEmpty -= 1
  }

  // If we found content, keep only paragraphs between first and last non-empty
  if (firstNonEmpty <= lastNonEmpty) {
    const cleanedParagraphs = paragraphs.slice(firstNonEmpty, lastNonEmpty + 1)
    out.replaceChildren(...cleanedParagraphs)
  } else if (paragraphs.length > 0) {
    // If all paragraphs are empty, keep just one
    const emptyP = document.createElement('p')
    emptyP.appendChild(document.createElement('br'))
    out.replaceChildren(emptyP)
  }

  return out.innerHTML
}
